import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { queryOperatorsProtected } from "../lib/operatorsApi";
import { useAuthStore } from "../stores/authStore";

export interface Zone {
  name: string;
  type: "city" | "region" | "tourist_area" | "municipality" | "operator";
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  } | null;
  polygon?: GeoJSON.Polygon; // Real polygon from OpenStreetMap if available
  count: number;
  // Additional fields for operators
  city?: string | null;
  region?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

// Predefined Italian regions with approximate bounds (will be refined by API)
const ITALIAN_REGIONS: Zone[] = [
  { name: "Abruzzo", type: "region", bounds: { north: 42.8, south: 41.7, east: 14.8, west: 13.0 }, count: 0 },
  { name: "Basilicata", type: "region", bounds: { north: 41.0, south: 39.8, east: 16.9, west: 15.2 }, count: 0 },
  { name: "Calabria", type: "region", bounds: { north: 40.2, south: 37.9, east: 17.2, west: 15.6 }, count: 0 },
  { name: "Campania", type: "region", bounds: { north: 41.6, south: 39.9, east: 15.8, west: 13.6 }, count: 0 },
  { name: "Emilia-Romagna", type: "region", bounds: { north: 45.2, south: 43.7, east: 12.8, west: 9.2 }, count: 0 },
  { name: "Friuli-Venezia Giulia", type: "region", bounds: { north: 46.7, south: 45.4, east: 13.9, west: 12.4 }, count: 0 },
  { name: "Lazio", type: "region", bounds: { north: 42.8, south: 41.0, east: 13.9, west: 11.4 }, count: 0 },
  { name: "Liguria", type: "region", bounds: { north: 44.7, south: 43.7, east: 9.8, west: 7.6 }, count: 0 },
  { name: "Lombardia", type: "region", bounds: { north: 46.7, south: 44.7, east: 11.1, west: 8.4 }, count: 0 },
  { name: "Marche", type: "region", bounds: { north: 43.9, south: 42.8, east: 13.9, west: 12.4 }, count: 0 },
  { name: "Molise", type: "region", bounds: { north: 42.0, south: 41.4, east: 15.1, west: 14.0 }, count: 0 },
  { name: "Piemonte", type: "region", bounds: { north: 46.7, south: 44.0, east: 8.8, west: 6.6 }, count: 0 },
  { name: "Puglia", type: "region", bounds: { north: 42.0, south: 39.8, east: 18.6, west: 15.0 }, count: 0 },
  { name: "Sardegna", type: "region", bounds: { north: 41.3, south: 38.8, east: 9.8, west: 8.0 }, count: 0 },
  { name: "Sicilia", type: "region", bounds: { north: 38.6, south: 36.6, east: 15.7, west: 12.4 }, count: 0 },
  { name: "Toscana", type: "region", bounds: { north: 44.5, south: 42.3, east: 12.4, west: 9.6 }, count: 0 },
  { name: "Trentino-Alto Adige", type: "region", bounds: { north: 47.1, south: 45.6, east: 12.5, west: 10.4 }, count: 0 },
  { name: "Umbria", type: "region", bounds: { north: 43.6, south: 42.4, east: 13.2, west: 11.9 }, count: 0 },
  { name: "Valle d'Aosta", type: "region", bounds: { north: 46.1, south: 45.6, east: 7.9, west: 6.6 }, count: 0 },
  { name: "Veneto", type: "region", bounds: { north: 46.7, south: 44.8, east: 13.1, west: 10.4 }, count: 0 },
];

// Normalize string for comparison: lowercase, remove hyphens/apostrophes, normalize spaces
const normalizeForComparison = (str: string): string => {
  if (!str) return "";
  return str.toLowerCase()
    .replace(/-/g, " ")
    .replace(/'/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

// Check if query matches a region (exact or partial match)
// Avoid false positives: "roma" should NOT match "Emilia-Romagna"
const matchRegion = (query: string, region: Zone): boolean => {
  const normalizedQuery = normalizeForComparison(query);
  const normalizedRegion = normalizeForComparison(region.name);

  // Exact match
  if (normalizedRegion === normalizedQuery) return true;

  // Partial match: region name starts with query (e.g., "trentino" -> "Trentino-Alto Adige")
  if (normalizedRegion.startsWith(normalizedQuery + " ")) return true;

  // Partial match: query matches first word of region name (e.g., "trentino" -> "Trentino-Alto Adige")
  const regionWords = normalizedRegion.split(" ");
  const firstWord = regionWords[0];
  if (firstWord && (firstWord.startsWith(normalizedQuery) || normalizedQuery.startsWith(firstWord))) {
    return true;
  }

  // For multi-word queries, check if all words match region name words
  const queryWords = normalizedQuery.split(" ");
  if (queryWords.length > 1) {
    // All query words must be found in region name
    return queryWords.every(qWord =>
      regionWords.some(rWord => rWord.startsWith(qWord) || qWord.startsWith(rWord))
    );
  }

  return false;
};

/**
 * Hook to search for zones (cities and regions) with autocomplete
 * Uses debounced query to prevent excessive API calls
 * Explicitly cancels previous queries when search term changes
 */
export function useZoneSearch(query: string, limit: number = 20) {
  const { token, isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  // Cancel all previous zone-search queries when query changes
  // TanStack Query will automatically cancel queries when queryKey changes,
  // but we explicitly cancel to ensure HTTP requests are aborted via AbortSignal
  useEffect(() => {
    // Cancel all queries with the zone-search prefix that don't match current query
    queryClient.cancelQueries({
      queryKey: ["zone-search"],
      exact: false,
      predicate: (queryState) => {
        // Don't cancel the current query - only cancel queries with different query/limit
        const queryKey = queryState.queryKey;
        const prevQuery = queryKey[1] as string;
        const prevLimit = queryKey[2] as number;
        return prevQuery !== query || prevLimit !== limit;
      },
    });
  }, [query, limit, queryClient]);

  // Filter predefined regions that match the query (instant results)
  const matchingRegions = useMemo(() => {
    if (!query || query.length < 2) return [];
    const matched = ITALIAN_REGIONS.filter(region => matchRegion(query, region));
    // Debug: log matches to verify they're correct
    if (matched.length > 0) {
      console.log(`[useZoneSearch] Found ${matched.length} matching regions for "${query}":`, matched.map(r => r.name));
    }
    return matched;
  }, [query]);

  return useQuery<Zone[]>({
    queryKey: ["zone-search", query, limit],
    queryFn: async ({ signal }) => {
      if (!query || query.length < 2) return [];

      // Always fetch from API to get all results (cities, regions, etc.)
      // TanStack Query automatically passes AbortSignal to cancel requests
      // when queryKey changes or query is cancelled
      console.log(`[useZoneSearch] Fetching from API for query: "${query}"`);
      const apiResults = await queryOperatorsProtected<Zone[]>(
        token!,
        "search_zones",
        {
          query,
          limit,
        },
        signal // Pass AbortSignal to cancel HTTP request when query is cancelled
      );
      console.log(`[useZoneSearch] API returned ${apiResults.length} results:`, apiResults.map(z => `${z.name} (${z.type}, count: ${z.count}, bounds: ${z.bounds ? 'yes' : 'null'})`));

      // Merge predefined regions with API results
      // Prefer API results if they exist (they have real polygons and accurate bounds)
      const apiRegionNames = new Set(apiResults.map(z => normalizeForComparison(z.name)));
      const predefinedRegionsNotInApi = matchingRegions.filter(
        region => !apiRegionNames.has(normalizeForComparison(region.name))
      );

      // Combine: API results first (they're more accurate), then predefined regions not found in API
      // Always return API results even if we have matching regions
      // Don't filter out zones without bounds - they're still valid for autocomplete
      const combined = [...apiResults, ...predefinedRegionsNotInApi].slice(0, limit);
      console.log(`[useZoneSearch] Combined results (${combined.length} total):`, combined.map(z => `${z.name} (${z.type}, bounds: ${z.bounds ? 'yes' : 'null'})`));
      return combined;
    },
    enabled: isAuthenticated && !!token && query.length >= 2,
    staleTime: 30000, // 30 seconds
    gcTime: 60000, // Keep in cache for 60 seconds (formerly cacheTime)
    // Return matching regions immediately while API call is in progress
    // This shows instant results for regions while API loads other results
    // But API will always be called to get all results
    placeholderData: matchingRegions.length > 0 ? matchingRegions : undefined,
  });
}

